import { useEffect, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/**
 * Re-run validation when the interface language changes so error messages
 * already on screen switch to the new language. The zod schema is rebuilt from
 * the current `t` on each render, so re-triggering validation regenerates the
 * messages in the active language.
 *
 * Only re-validates when the form was already submitted or already shows
 * errors, so switching language on an untouched form does not surface errors.
 */
export function useRevalidateOnLanguageChange<T extends FieldValues>(form: UseFormReturn<T>) {
	const { i18n } = useTranslation();
	const language = i18n.language;
	const isFirstRun = useRef(true);

	useEffect(() => {
		if (isFirstRun.current) {
			isFirstRun.current = false;
			return;
		}
		const { isSubmitted, errors } = form.formState;
		if (isSubmitted || Object.keys(errors).length > 0) {
			void form.trigger();
		}
		// form is stable across renders; only re-run on language change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [language]);
}
